import { ReactNode, createContext } from "react";
import useTerceiro, { TerceiroType, CreateTerceiroType, EditTerceiroType, TerceiroWithConfirmType } from "./hooks/useTerceiro";

interface TerceiroContextProviderProps {
    children: ReactNode;
}

export interface TerceiroContextData {
    createTerceiro: (createTerceiroData: CreateTerceiroType) => Promise<CreateTerceiroType | null>;
    getTerceiroPaged: (
        pageNumber: number,
        Name?: string,
        EmpresaId?: number,
        pageSize?: number,
        tipoTerceiroName?: string
    ) => Promise<TerceiroType[] | null>;
    updateTerceiro: (editTerceiroData: EditTerceiroType) => Promise<EditTerceiroType | null>;
    deleteTerceiro: (terceiroId: number) => Promise<TerceiroType | null>;
    handleGetTerceiroByUserId: (userId: number) => Promise<TerceiroType | null>;
    handleGetTerceiroById: (terceiroId: number) => Promise<TerceiroType | null>;
    setTerceiro: any;
    terceiro: TerceiroType;
    listTerceiro: TerceiroType[];
    initialListTerceiro: TerceiroType[];
    setListTerceiro: (terceiros: TerceiroType[]) => void;
    setPageCount: (count: number) => void;
    pageCount: number;
    handleGetTerceiroByAgendaId: (agendaId: number) => Promise<TerceiroWithConfirmType[] | null>;
    listTerceiroByAgenda: TerceiroWithConfirmType[];
    listEditor: TerceiroType[];
    handleGetEditorsByEmpresa: (empresaId: number) => Promise<TerceiroType[] | null>;
    listLaboratory: TerceiroType[];
    handleGetLaboratoriesByEmpresa: (empresaId: number) => Promise<TerceiroType[] | null>;
    listVendedores: TerceiroType[];
    handleGetVendedoresByEmpresa: (empresaId: number) => Promise<TerceiroType[] | null>;
    listRepresentanteComercial: TerceiroType[];
    handleGetRepresentanteComercialByEmpresa: (empresaId: number) => Promise<TerceiroType[] | null>;
    listFotografos: TerceiroType[];
    handleGetFotografosByEmpresa: (empresaId: number) => Promise<TerceiroType[] | null>;
    // removeServicoDoTerceiro: (terceiroId: number, tipoServicoId: number) => Promise<boolean>;
    handleGetServicosByTerceiro: (terceiroId: number) => Promise<any[] | null>;
    addServicoToTerceiro: (p: { terceiroId: number; tipoServicoId: number; value: number }) => Promise<boolean>;
    updateValorServicoTerceiro: (p: { terceiroId: number; tipoServicoId: number; value: number }) => Promise<boolean>;
    removeServicoDoTerceiro: (terceiroId: number, tipoServicoId: number) => Promise<boolean>;
    removeServicoFromTerceiro: (p: { terceiroId: number; tipoServicoId: number }) => Promise<boolean>;
}

const initialState: TerceiroContextData = {
    createTerceiro: async () => null,
    getTerceiroPaged: async () => null,
    updateTerceiro: async () => null,
    deleteTerceiro: async () => null,
    handleGetTerceiroByUserId: async () => null,
    handleGetTerceiroById: async () => null,
    setTerceiro: () => { },
    terceiro: {
        id: 0,
        name: "",
        email: "",
        cpf: "",
        telephone: "",
        telephone_01: "",
        address: "",
        addressNumber: "",
        neighborhood: "",
        city: "",
        state: "",
        zipCode: "",
        landline: "",
        bank: "",
        agency: "",
        currentAccount: "",
        observation: "",
        pix: "",
        spendingCeiling: 0,
        balanceExpenses: 0,
        isManager: false,
        userId: 0,
        empresaId: 0,
        tipoTerceiroId: 0,
        tipoTerceiroName: "",
    },
    listTerceiro: [],
    initialListTerceiro: [],
    setListTerceiro: () => { },
    setPageCount: () => { },
    pageCount: 0,
    handleGetTerceiroByAgendaId: async () => null,
    listTerceiroByAgenda: [],
    listEditor: [],
    handleGetEditorsByEmpresa: async () => null,
    listLaboratory: [],
    handleGetLaboratoriesByEmpresa: async () => null,
    listVendedores: [],
    handleGetVendedoresByEmpresa: async () => null,
    listRepresentanteComercial: [],
    handleGetRepresentanteComercialByEmpresa: async () => null,
    listFotografos: [],
    handleGetFotografosByEmpresa: async () => null,
    // removeServicoDoTerceiro: async () => false,
    handleGetServicosByTerceiro: async () => null,
    addServicoToTerceiro: async () => false,
    updateValorServicoTerceiro: async () => false,
    removeServicoDoTerceiro: async () => false,
    removeServicoFromTerceiro: async () => false,
};

export const TerceiroContext =
    createContext<TerceiroContextData>(initialState);

export default function TerceiroContextProvider({
    children,
}: TerceiroContextProviderProps) {
    const {
        createTerceiro,
        getTerceiroPaged,
        updateTerceiro,
        deleteTerceiro,
        handleGetTerceiroByUserId,
        handleGetTerceiroById,
        terceiro,
        listTerceiro,
        initialListTerceiro,
        setListTerceiro,
        setPageCount,
        pageCount,
        handleGetTerceiroByAgendaId,
        listTerceiroByAgenda,
        listEditor,
        handleGetEditorsByEmpresa,
        listLaboratory,
        handleGetLaboratoriesByEmpresa,
        listVendedores,
        handleGetVendedoresByEmpresa,
        listRepresentanteComercial,
        handleGetRepresentanteComercialByEmpresa,
        listFotografos,
        handleGetFotografosByEmpresa,
        // removeServicoDoTerceiro,
        handleGetServicosByTerceiro,
        addServicoToTerceiro,
        updateValorServicoTerceiro,
        removeServicoDoTerceiro,
        removeServicoFromTerceiro,
    } = useTerceiro();

    return (
        <TerceiroContext.Provider
            value={{
                createTerceiro,
                getTerceiroPaged,
                updateTerceiro,
                deleteTerceiro,
                handleGetTerceiroByUserId,
                handleGetTerceiroById,
                setTerceiro: () => { },
                terceiro,
                listTerceiro,
                initialListTerceiro,
                setListTerceiro,
                setPageCount,
                pageCount,
                handleGetTerceiroByAgendaId,
                listTerceiroByAgenda,
                listEditor,
                handleGetEditorsByEmpresa,
                listLaboratory,
                handleGetLaboratoriesByEmpresa,
                listVendedores,
                handleGetVendedoresByEmpresa,
                listRepresentanteComercial,
                handleGetRepresentanteComercialByEmpresa,
                listFotografos,
                handleGetFotografosByEmpresa,
                // removeServicoDoTerceiro,
                handleGetServicosByTerceiro,
                addServicoToTerceiro,
                updateValorServicoTerceiro,
                removeServicoDoTerceiro,
                removeServicoFromTerceiro,
            }}
        >
            {children}
        </TerceiroContext.Provider>
    );
}
